import React, { useEffect, useMemo, useState } from 'react';
import { Badge, InputGroup, Form, Spinner } from 'react-bootstrap';
import { FaSearch, FaPoll, FaChartBar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';

const InstructorSurveyList = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.surveys.getAll({ limit: 100 })
      .then((res) => {
        const d = res.data?.data || res.data?.surveys || [];
        setSurveys(Array.isArray(d) ? d : []);
      })
      .catch((err) => toast.error('Failed to load surveys: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return surveys;
    return surveys.filter((s) =>
      (s.title || '').toLowerCase().includes(term) ||
      (s.description || '').toLowerCase().includes(term)
    );
  }, [surveys, search]);

  return (
    <div className="p-3 sm:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="mb-5">
        <h4 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-red-600 mb-1">
          Surveys
        </h4>
        <p className="text-sm text-gray-500">View survey analytics for available surveys.</p>
      </div>

      <div className="mb-4 max-w-sm">
        <InputGroup className="shadow-sm rounded-xl overflow-hidden">
          <InputGroup.Text className="bg-white border-0 text-purple-600 px-3"><FaSearch /></InputGroup.Text>
          <Form.Control type="search" placeholder="Search surveys…" value={search}
            onChange={(e) => setSearch(e.target.value)} className="border-0 text-sm" />
        </InputGroup>
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <Spinner animation="border" variant="primary" style={{ width: 48, height: 48 }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white/80 rounded-2xl shadow border border-purple-100">
          <FaPoll size={48} className="text-purple-300 mx-auto mb-3" />
          <p className="text-purple-700 font-medium">{search ? 'No surveys match your search.' : 'No surveys available.'}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl shadow-lg bg-white">
          <table className="w-full text-sm table m-0">
            <thead className="bg-gradient-to-r from-purple-500 to-red-600 text-white">
              <tr>
                <th className="py-3 px-4 text-left font-bold">Title</th>
                <th className="py-3 px-4 text-left font-bold hidden md:table-cell">Description</th>
                <th className="py-3 px-4 text-left font-bold">Type</th>
                <th className="py-3 px-4 text-left font-bold">Status</th>
                <th className="py-3 px-4 text-left font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((survey) => (
                <tr key={survey.id} className="border-b border-purple-50 hover:bg-purple-50/40">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {survey.image ? (
                        <img src={getAssetUrl(survey.image)} alt={survey.title}
                          className="w-9 h-9 rounded-lg object-cover shadow"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center shadow">
                          <FaPoll className="text-white w-4 h-4" />
                        </div>
                      )}
                      <span className="font-semibold text-gray-800">{survey.title}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-500 max-w-xs truncate hidden md:table-cell">{survey.description || '—'}</td>
                  <td className="py-3 px-4">
                    <Badge bg={survey.survey_type === 'global' ? 'primary' : 'info'} className="text-xs text-capitalize">
                      {survey.survey_type || 'organization'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <Badge bg={survey.status === 1 ? 'success' : 'secondary'} className="text-xs">
                      {survey.status === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/instructor/surveys/${survey.id}/responses`)}
                      className="flex items-center gap-1 text-purple-600 hover:text-purple-800 text-xs font-semibold border-0 bg-transparent"
                      tabIndex={0}
                      aria-label={`View analytics for ${survey.title}`}
                    >
                      <FaChartBar /> Analytics
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InstructorSurveyList;
